import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'
import { PRM_CombinedResponse } from '../types/unified_models'

interface UseSubmissionPollingOptions {
  token: string | null
  onComplete: (response: PRM_CombinedResponse) => void
  onError: (error: string) => void
  pollInterval?: number
  maxPollTime?: number
}

export const useSubmissionPolling = ({
  token,
  onComplete,
  onError,
  pollInterval = 2000, // 2 seconds
  maxPollTime = 300000, // 5 minutes
}: UseSubmissionPollingOptions) => {
  const { getAccessToken } = useAuth()
  const [isPolling, setIsPolling] = useState(false)
  const [pollCount, setPollCount] = useState(0)
  const [startTime, setStartTime] = useState<number | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isActiveRef = useRef(false)
  const tokenRef = useRef<string | null>(null)
  const onCompleteRef = useRef(onComplete)
  const onErrorRef = useRef(onError)
  const startTimeRef = useRef<number | null>(null)

  // Update refs when props change
  tokenRef.current = token
  onCompleteRef.current = onComplete
  onErrorRef.current = onError
  startTimeRef.current = startTime

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    isActiveRef.current = false
    setIsPolling(false)
  }

  const pollSubmission = async () => {
    const currentToken = tokenRef.current
    if (!currentToken || !isActiveRef.current) return

    try {
      const authToken = await getAccessToken()
      const response = await api.get(`/submissions/${currentToken}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })

      const data: PRM_CombinedResponse = response.data

      // Check if we have a complete result (any of the response fields have data)
      const hasCompleteResult = data.correctness || data.benchmark || data.custom || data.tiers || data.cilksan || data.compiler_analysis
      
      if (hasCompleteResult) {
        stopPolling()
        onCompleteRef.current(data)
        return
      }

      // Check if we've exceeded the maximum poll time
      const currentStartTime = startTimeRef.current
      if (currentStartTime && Date.now() - currentStartTime > maxPollTime) {
        stopPolling()
        onErrorRef.current('Submission timed out. Please try again.')
        return
      }

      // Continue polling
      setPollCount(prev => prev + 1)
    } catch (error) {
      console.error('Error polling submission:', error)
      stopPolling()
      onErrorRef.current('Failed to check submission status. Please try again.')
    }
  }

  useEffect(() => {
    if (!token) {
      stopPolling()
      return
    }

    // Clean up any existing polling
    stopPolling()

    // Initialize polling state
    setPollCount(0)
    const now = Date.now()
    setStartTime(now)
    startTimeRef.current = now
    setIsPolling(true)
    isActiveRef.current = true

    // Start polling immediately
    pollSubmission()

    // Set up interval for polling
    const newInterval = setInterval(() => {
      if (isActiveRef.current) {
        pollSubmission()
      }
    }, pollInterval)
    
    intervalRef.current = newInterval

    return () => {
      stopPolling()
    }
  }, [token, pollInterval])

  return {
    isPolling,
    pollCount,
    startTime,
  }
} 